import * as THREE from 'three';
import { createViewerBridge } from '../src/bridge/viewerBridge';
import { bootstrap } from '../src/auth/bootstrap';
import { SceneManager } from './scene/SceneManager';
import { setupEnvironment } from './scene/Environment';
import { WindowGroup } from './scene/WindowGroup';
import { WindowState } from './state';
import { MaterialLibrary } from './materials/MaterialLibrary';
import { AnimationController } from './animation/AnimationController';
import { Panel } from './ui/Panel';
import { SectionType, MaterialFamily, GlazingTier } from './types';
import { mm } from './utils/units';

const isDevMode = new URLSearchParams(window.location.search).has('dev');

async function main(): Promise<void> {
  // Auth bootstrap (dev bypass on localhost)
  try {
    await bootstrap({
      supabaseUrl: (import.meta as any).env?.VITE_SUPABASE_URL || '',
      supabaseAnonKey: (import.meta as any).env?.VITE_SUPABASE_ANON_KEY,
    });
  } catch {
    // Dev mode or no auth configured — continue
  }

  // State
  const state = new WindowState();

  // SDK bridge
  const bridge = createViewerBridge({
    viewerType: 'windows',
    schemaVersion: '1.0.0',
    onInit: (options) => {
      applyOptions(state, options);
    },
    onSetOption: (key, value) => {
      applyOption(state, key, value);
    },
    onSetOptions: (patch) => {
      applyOptions(state, patch);
    },
    onCommit: () => {
      bridge.sendState(state.toJSON());
    },
  });

  // Dev mode: show panel
  if (isDevMode) {
    document.body.classList.add('dev-mode');
  }

  // Scene
  const container = document.getElementById('viewer-container')!;
  const sceneManager = new SceneManager(container);
  setupEnvironment(sceneManager.scene);

  // Materials
  const materialLib = new MaterialLibrary();

  // Window
  const windowGroup = new WindowGroup();
  sceneManager.addToScene(windowGroup.group);

  // Animation
  const animController = new AnimationController(state);

  // Initial build
  windowGroup.rebuild(state.config, materialLib);
  sceneManager.frameWindow(mm(state.config.height), mm(state.config.width));

  // Panel (dev mode only)
  let panel: Panel | null = null;
  if (isDevMode) {
    const panelContainer = document.getElementById('options-panel')!;
    panel = new Panel(panelContainer, state);
  }

  // Track if rebuild is needed
  let needsRebuild = false;
  let needsReframe = false;
  let prevWidth = state.config.width;
  let prevHeight = state.config.height;
  let prevLayoutId = state.config.layout.presetId;

  state.subscribe(() => {
    needsRebuild = true;

    // Check if dimensions or layout changed -> reframe camera
    if (state.config.width !== prevWidth || state.config.height !== prevHeight) {
      needsReframe = true;
      prevWidth = state.config.width;
      prevHeight = state.config.height;
    }

    // If layout changed -> rebuild panel
    if (state.config.layout.presetId !== prevLayoutId) {
      prevLayoutId = state.config.layout.presetId;
      if (panel) panel.rebuild();
    }

    // Sync to parent
    bridge.sendState(state.toJSON());
  });

  // Click-to-toggle interaction
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let lastClickTime = 0;

  container.addEventListener('click', (event) => {
    const rect = container.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(pointer, sceneManager.camera);
    const intersects = raycaster.intersectObjects(windowGroup.group.children, true);

    for (const hit of intersects) {
      const sectionIndex = findSectionIndex(hit.object);
      if (sectionIndex !== null) {
        const now = Date.now();
        const isDouble = (now - lastClickTime) < 350;
        lastClickTime = now;

        const config = state.config.sections[sectionIndex];
        if (config && config.type === SectionType.TT && isDouble) {
          animController.turnSection(sectionIndex);
        } else if (config && config.type !== SectionType.FIX) {
          animController.toggleSection(sectionIndex, windowGroup.getSectionInterfaces());
        }
        break;
      }
    }
  });

  // Hover cursor
  container.addEventListener('mousemove', (event) => {
    const rect = container.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(pointer, sceneManager.camera);
    const intersects = raycaster.intersectObjects(windowGroup.group.children, true);

    let isOverSection = false;
    for (const hit of intersects) {
      const idx = findSectionIndex(hit.object);
      if (idx !== null) {
        const cfg = state.config.sections[idx];
        if (cfg && cfg.type !== SectionType.FIX) {
          isOverSection = true;
          break;
        }
      }
    }
    container.style.cursor = isOverSection ? 'pointer' : 'default';
  });

  // Render loop
  sceneManager.startLoop((dt) => {
    if (needsRebuild && !animController.isAnimating) {
      windowGroup.rebuild(state.config, materialLib);
      needsRebuild = false;
    }

    if (needsReframe) {
      sceneManager.frameWindow(mm(state.config.height), mm(state.config.width));
      needsReframe = false;
    }

    animController.update(dt, windowGroup.getSectionInterfaces());
  });

  // Start bridge (sends READY to parent)
  bridge.start();
}

function findSectionIndex(object: THREE.Object3D): number | null {
  let current: THREE.Object3D | null = object;
  while (current) {
    if (current.userData?.sectionIndex != null) {
      return current.userData.sectionIndex - 1;
    }
    current = current.parent;
  }
  return null;
}

function applyOption(state: WindowState, key: string, value: unknown): void {
  switch (key) {
    case 'width':
      state.setDimensions(value as number, state.config.height);
      break;
    case 'height':
      state.setDimensions(state.config.width, value as number);
      break;
    case 'layout':
      state.setLayout(value as string);
      break;
    case 'frameMaterial':
      state.setFrameMaterial(value as MaterialFamily);
      break;
    case 'glazingTier':
      state.setGlazingTier(value as GlazingTier);
      break;
    case 'mirrorLR':
      state.setMirror(value as boolean);
      break;
    case 'flipTB':
      state.setFlip(value as boolean);
      break;
  }
}

function applyOptions(state: WindowState, options: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(options)) {
    applyOption(state, key, value);
  }
}

main().catch(console.error);
